"use client";

import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase/client";
import { 
  Save, Globe, Truck, Percent, Share2, 
  Mail, Phone, MapPin, Loader2, UploadCloud, 
  Layout, CheckCircle2, AlertCircle, X
} from "lucide-react";

export default function SiteSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Refs for Image Uploads
  const logoRef = useRef(null);
  const faviconRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  async function fetchSettings() {
    setLoading(true);
    const { data, error } = await supabase
      .from("site_settings")
      .select("*")
      .single(); // Settings usually only has one row

    if (!error && data) {
      setSettings(data);
    }
    setLoading(false);
  }

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setSettings(prev => ({ 
        ...prev, 
        [name]: type === "number" ? (value === "" ? null : Number(value)) : value 
      }));
  };

  const handleSocialChange = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value }
    }));
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setSaving(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${field}-${Date.now()}.${fileExt}`;
      const path = `settings/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("cms-media")
        .upload(path, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("cms-media").getPublicUrl(path);
      setSettings(prev => ({ ...prev, [field]: urlData.publicUrl }));
      
      setMessage({ type: "success", text: `${field.replace('_', ' ')} updated in preview.` });
    } catch (err) {
      setMessage({ type: "error", text: "Upload failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const { error } = await supabase
        .from("site_settings")
        .update({
          ...settings,
          updated_at: new Date().toISOString()
        })
        .eq("id", settings.id);

      if (error) throw error;
      setMessage({ type: "success", text: "Global configurations saved successfully!" });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 4000);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <Loader2 className="animate-spin text-indigo-600" size={40} />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-10 font-sans text-slate-900">
      <div className="max-w-5xl mx-auto">
        
        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">System Settings</h1>
            <p className="text-slate-500 text-sm">Configure global store behavior and metadata.</p>
          </div>
          <button 
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 text-white px-8 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-indigo-700 shadow-xl shadow-indigo-200 transition-all disabled:opacity-50"
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Commit Changes
          </button>
        </div>

        {/* --- FEEDBACK TOAST --- */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 border animate-in fade-in slide-in-from-top-2 ${
            message.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-red-50 border-red-100 text-red-700"
          }`}>
            {message.type === "success" ? <CheckCircle2 size={20}/> : <AlertCircle size={20}/>}
            <span className="text-sm font-bold">{message.text}</span>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-8 pb-20">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* --- LEFT: GENERAL & ASSETS --- */}
            <div className="lg:col-span-7 space-y-8">
              
              {/* Branding Section */}
              <section className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm space-y-6">
                <h3 className="text-xs font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-2">
                  <Layout size={14}/> Branding & Identity
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-full">
                    <label className="text-[11px] font-bold text-slate-500 mb-1.5 block px-1">Store Name</label>
                    <input 
                      name="site_name"
                      value={settings.site_name}
                      onChange={handleChange}
                      className="w-full bg-slate-50 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>

                  {/* Logo Upload */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-bold text-slate-500 block px-1">Main Logo</label>
                    <div 
                      onClick={() => logoRef.current.click()}
                      className="aspect-video bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors group overflow-hidden"
                    >
                      {settings.logo_url ? (
                        <img src={settings.logo_url} className="w-full h-full object-contain p-4" alt="Logo" />
                      ) : (
                        <UploadCloud size={24} className="text-slate-300 group-hover:text-indigo-500" />
                      )}
                      <input type="file" ref={logoRef} hidden onChange={(e) => handleImageUpload(e, 'logo_url')} />
                    </div>
                  </div>

                  {/* Favicon Upload */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-bold text-slate-500 block px-1">Favicon (1:1)</label>
                    <div 
                      onClick={() => faviconRef.current.click()}
                      className="aspect-video bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors group overflow-hidden"
                    >
                      {settings.favicon_url ? (
                        <img src={settings.favicon_url} className="w-12 h-12 object-contain" alt="Favicon" />
                      ) : (
                        <Globe size={24} className="text-slate-300 group-hover:text-indigo-500" />
                      )}
                      <input type="file" ref={faviconRef} hidden onChange={(e) => handleImageUpload(e, 'favicon_url')} />
                    </div>
                  </div>
                </div>
              </section>

              {/* Support Section */}
              <section className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm space-y-6">
                <h3 className="text-xs font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-2">
                  <Phone size={14}/> Communication
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1.5 block px-1">Support Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/>
                      <input name="support_email" value={settings.support_email} onChange={handleChange} className="pl-10 w-full bg-slate-50 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1.5 block px-1">Support Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/>
                      <input name="support_number" value={settings.support_number} onChange={handleChange} className="pl-10 w-full bg-slate-50 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                    </div>
                  </div>
                  <div className="col-span-full">
                    <label className="text-[11px] font-bold text-slate-500 mb-1.5 block px-1">Office Address</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-4 text-slate-400" size={16}/>
                      <textarea name="office_address" value={settings.office_address} onChange={handleChange} className="pl-10 w-full bg-slate-50 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-24 resize-none" />
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* --- RIGHT: LOGISTICS & SEO --- */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Shipping & Tax */}
              <section className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm space-y-6">
                <h3 className="text-xs font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-2">
                  <Truck size={14}/> Logistics & Rates
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                    <span className="text-sm font-bold text-slate-600">Standard Inside Fee</span>
                    <input type="number" name="inside_fee" value={settings.inside_fee} onChange={handleChange} className="w-20 bg-white border border-slate-200 rounded-lg p-2 text-right text-sm font-black" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                <span className="text-sm font-bold text-slate-600">Sub-Inside Fee</span>
                <input 
                    type="number" 
                    name="sub_inside" 
                    value={settings.sub_inside || ""} 
                    onChange={handleChange} 
                    className="w-20 bg-white border border-slate-200 rounded-lg p-2 text-right text-sm font-black" 
                />
                </div>

                {/* Outside Fee */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                <span className="text-sm font-bold text-slate-600">Outside Fee</span>
                <input 
                    type="number" 
                    name="outside" 
                    value={settings.outside || ""} 
                    onChange={handleChange} 
                    className="w-20 bg-white border border-slate-200 rounded-lg p-2 text-right text-sm font-black" 
                />
                </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                    <span className="text-sm font-bold text-slate-600">Free Shipping on </span>
                    <input type="number" name="shipping_threshold" value={settings.shipping_threshold} onChange={handleChange} className="w-24 bg-white border border-slate-200 rounded-lg p-2 text-right text-sm font-black" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100">
                    <span className="text-sm font-bold text-indigo-600 flex items-center gap-2"><Percent size={14}/> Sales Tax (%)</span>
                    <input type="number" name="tax_percentage" value={settings.tax_percentage} onChange={handleChange} className="w-20 bg-white border border-indigo-200 rounded-lg p-2 text-right text-sm font-black text-indigo-600" />
                  </div>
                </div>
              </section>

              {/* SEO Section */}
              <section className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm space-y-6">
                <h3 className="text-xs font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-2">
                  <Globe size={14}/> SEO Metadata
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1.5 block px-1">Meta Title</label>
                    <input name="meta_title" value={settings.meta_title || ""} onChange={handleChange} className="w-full bg-slate-50 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Search engine title..." />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1.5 block px-1">Meta Description</label>
                    <textarea name="meta_description" value={settings.meta_description || ""} onChange={handleChange} className="w-full bg-slate-50 border-none rounded-xl p-3 text-sm h-24 resize-none outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Brief summary for Google..." />
                  </div>
                </div>
              </section>

              {/* Social Links */}
              <section className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm space-y-6">
  <div className="flex justify-between items-center">
    <h3 className="text-xs font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-2">
      <Share2 size={14}/> Social Connectivity
    </h3>
    <button 
      type="button"
      onClick={() => {
        const key = prompt("Enter platform name (e.g., twitter, whatsapp):");
        if (key) handleSocialChange(key.toLowerCase(), "");
      }}
      className="text-[10px] font-bold text-indigo-600 hover:underline"
    >
      + Add Platform
    </button>
  </div>

  <div className="space-y-3">
    {Object.entries(settings.social_links || {}).map(([platform, url]) => (
      <div key={platform} className="flex items-center gap-3 group">
        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-[10px] uppercase">
          {platform.slice(0, 2)}
        </div>
        <input 
          value={url} 
          onChange={(e) => handleSocialChange(platform, e.target.value)}
          placeholder={`${platform.charAt(0).toUpperCase() + platform.slice(1)} URL`} 
          className="flex-1 bg-slate-50 border-none rounded-xl p-2.5 text-xs outline-none focus:ring-1 focus:ring-indigo-500" 
        />
        <button 
          type="button"
          onClick={() => {
            const updatedLinks = { ...settings.social_links };
            delete updatedLinks[platform];
            setSettings({ ...settings, social_links: updatedLinks });
          }}
          className="opacity-0 group-hover:opacity-100 p-2 text-slate-300 hover:text-red-500 transition-all"
        >
          <X size={14} />
        </button>
      </div>
    ))}
  </div>
</section>
            </div>
          </div>
        </form>

      </div>
    </div>
  );
}